import React, { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export function ChatPanel({ url, me, height = "h-80" }: { url: string; me: "vendor" | "customer"; height?: string }) {
  const [msgs, setMsgs] = useState<any[]>([]);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get(url).then((r) => {
        if (!alive) return;
        if (Array.isArray(r.data)) { setMsgs(r.data); setError(""); }
        else setError("Chat is temporarily unavailable. Please refresh.");
      }).catch((e) => alive && setError(errMsg(e)));
    load();
    const t = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [url]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [msgs.length]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(url, { body: text });
      if (data?.id) setMsgs((m) => [...m, data]);
      setText("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="chat-panel">
      <div className={`${height} space-y-3 overflow-y-auto rounded-xl border bg-muted/40 p-3`}>
        {error && <p className="py-2 text-center text-sm text-rose-600" data-testid="chat-error">{error}</p>}
        {!error && msgs.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">No messages yet. Say hello.</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender === me ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.id}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === me ? "bg-emerald-600 text-white" : "bg-card border"}`}>
              <p>{String(m.body ?? "")}</p>
              <p className={`mt-1 text-[10px] ${m.sender === me ? "text-orange-100" : "text-muted-foreground"}`}>{m.sender_name} · {m.created_at ? timeAgo(m.created_at) : ""}</p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message..." data-testid="chat-input" />
        <Button type="submit" size="icon" className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="chat-send-button">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
